<template>
  <template v-if="store.isLoadingPaymentRequestPeriods" />
  <template v-else-if="showsPeriodPicker">
    <InputContainer :read-only="!store.isEditable">
      <template #label>{{ $t('Period') }}</template>
      <template #sub-label>
        <span v-if="store.hasNoPaymentRequestPeriodToBill" data-test="payment-request-no-period">
          {{
            $t(
              'There’s no week to bill yet. Weeks already billed for time and future weeks can’t be picked.',
            )
          }}
        </span>
        <template v-else>{{ $t('The period this payment request covers') }}</template>
      </template>
      <template #read-only>
        <span data-test="payment-request-period-label">{{ readOnlyPeriodLabel }}</span>
      </template>
      <WdsSelect
        v-model="selectedPeriod"
        data-test="payment-request-period-select"
        class="tw-w-[300px] tw-max-w-full"
        :placeholder="$t('Select a period...')"
        :list="periodOptions"
        :disabled="store.hasNoPaymentRequestPeriodToBill"
        :error="
          billsDaysBeforeStart
            ? undefined
            : (validationStore.v$.dateStart.$errors?.[0]?.$message as string)
        "
      />
    </InputContainer>
    <template v-if="billsDaysBeforeStart && store.isEditable">
      <InputContainer>
        <template #label>{{ $t('From date') }}</template>
        <template #sub-label>{{ $t('The first billable day') }}</template>
        <PickerDatetime
          v-model="store.paymentRequestForm.dateStart"
          data-test="payment-request-date-start-picker"
          name="date_start"
          format="YYYY-MM-DD"
          :max-date="lastDayBeforeStart"
          :default-view-date="weekBeforeStart"
          :error="validationStore.v$.dateStart.$errors?.[0]?.$message as string"
          @update:model-value="validationStore.v$.dateEnd.$touch()"
          @blur="validationStore.v$.dateStart.$touch()"
        />
      </InputContainer>
      <InputContainer>
        <template #label>{{ $t('To date') }}</template>
        <template #sub-label>{{ $t('The last billable day') }}</template>
        <PickerDatetime
          v-model="store.paymentRequestForm.dateEnd"
          data-test="payment-request-date-end-picker"
          name="date_end"
          format="YYYY-MM-DD"
          :min-date="store.paymentRequestForm.dateStart ?? ''"
          :max-date="lastDayBeforeStart"
          :default-view-date="weekBeforeStart"
          :error="validationStore.v$.dateEnd.$errors?.[0]?.$message as string"
          @blur="validationStore.v$.dateEnd.$touch()"
        />
      </InputContainer>
    </template>
  </template>
  <template v-else>
    <InputContainer :read-only="!store.isEditable">
      <template #label>{{ $t('From date') }}</template>
      <template #sub-label>{{ $t('The first billable day') }}</template>
      <template #read-only>{{ store.paymentRequestForm.dateStart }}</template>
      <PickerDatetime
        v-model="store.paymentRequestForm.dateStart"
        data-test="payment-request-date-start-picker"
        name="date_start"
        format="YYYY-MM-DD"
        :error="validationStore.v$.dateStart.$errors?.[0]?.$message as string"
        @update:model-value="validationStore.v$.dateEnd.$touch()"
        @blur="validationStore.v$.dateStart.$touch()"
      />
    </InputContainer>
    <InputContainer :read-only="!store.isEditable">
      <template #label>{{ $t('To date') }}</template>
      <template #sub-label>{{ $t('The last billable day') }}</template>
      <template #read-only>{{ store.paymentRequestForm.dateEnd || $t('None') }}</template>
      <PickerDatetime
        v-model="store.paymentRequestForm.dateEnd"
        data-test="payment-request-date-end-picker"
        name="date_end"
        format="YYYY-MM-DD"
        :min-date="store.paymentRequestForm.dateEnd ? store.paymentRequestForm.dateStart : ''"
        :error="validationStore.v$.dateEnd.$errors?.[0]?.$message as string"
        @blur="validationStore.v$.dateEnd.$touch()"
      />
    </InputContainer>
  </template>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import moment from 'moment'
import { WdsSelect } from '@worksome/wds-components'
import InputContainer from '@/components/PaymentRequest/InputContainer.vue'
import PickerDatetime from '@/components/PickerDatetime.vue'
import { usePaymentRequestStore } from '@/components/PaymentRequest/store/usePaymentRequestStore'
import { useTranslation } from '@/composables/translation'
import { useValidationStore } from '@/components/PaymentRequest/store/useValidationStore'
import { PaymentRequestTypeEnum } from '@/components/PaymentRequest/enums'
import {
  formatPeriodDate,
  paymentRequestPeriodLabel,
} from '@/components/PaymentRequest/utilities/paymentRequestPeriodLabel'

const { $t } = useTranslation()
const store = usePaymentRequestStore()
const validationStore = useValidationStore()

const DAYS_BEFORE_START = 'days-before-start'

const periodValue = (startDate: string, endDate: string) => `${startDate}|${endDate}`

// Milestone payment requests are exempt from the period rule, so they keep free dates.
const showsPeriodPicker = computed(
  () =>
    store.requiresPaymentRequestPeriods &&
    store.paymentRequestType !== PaymentRequestTypeEnum.MILESTONE,
)

const lastDayBeforeStart = computed(() =>
  store.paymentRequestPeriodsStartDate
    ? moment(store.paymentRequestPeriodsStartDate, 'YYYY-MM-DD').subtract(1, 'day').format('YYYY-MM-DD')
    : '',
)

// The calendars open on the last full week before the start date, where the days left to bill are.
const weekBeforeStart = computed(() =>
  store.paymentRequestPeriodsStartDate
    ? moment(store.paymentRequestPeriodsStartDate, 'YYYY-MM-DD').subtract(7, 'days').format('YYYY-MM-DD')
    : '',
)

// Days before the rule starts keep free dates, entered once the worker picks that option.
const picksDaysBeforeStart = ref(false)
const billsDaysBeforeStart = computed(() => {
  const { dateStart, dateEnd } = store.paymentRequestForm
  const lastDay = dateEnd || dateStart

  return (
    !!store.paymentRequestPeriodsStartDate &&
    (picksDaysBeforeStart.value || (!!lastDay && lastDay <= lastDayBeforeStart.value))
  )
})

// A picked week that drops out of the list, for instance when switching to Time x Rate, is unpicked.
watch(
  () => store.selectablePaymentRequestPeriods,
  (periods) => {
    const { dateStart, dateEnd } = store.paymentRequestForm
    const picked = store.paymentRequestPeriods.find(
      (period) => period.startDate === dateStart && period.endDate === dateEnd,
    )

    if (picked && !periods.includes(picked)) {
      store.paymentRequestForm.dateStart = undefined
      store.paymentRequestForm.dateEnd = ''
    }
  },
)

// A draft opened on a week that has since been billed says so straight away.
watch(
  () => store.pickedPaymentRequestPeriodIsBilled,
  (isBilled) => {
    if (isBilled) {
      validationStore.v$.dateStart.$touch()
    }
  },
  { immediate: true },
)

// A submitted payment request keeps a period saved before the rule applied while it is unchanged, so that
// period is offered alongside the hire's periods. A draft must pick a period before it is saved or sent.
const periodOptions = computed(() => {
  const options = store.selectablePaymentRequestPeriods.map((period) => ({
    value: periodValue(period.startDate, period.endDate),
    label: paymentRequestPeriodLabel(period.isoWeek, period.startDate, period.endDate),
  }))
  const saved = store.savedBillPeriod

  if (!store.isDraft && saved?.dateStart && saved.dateEnd && !billsDaysBeforeStart.value) {
    const value = periodValue(saved.dateStart, saved.dateEnd)

    if (!options.some((option) => option.value === value)) {
      options.unshift({ value, label: paymentRequestPeriodLabel(null, saved.dateStart, saved.dateEnd) })
    }
  }

  if (store.paymentRequestPeriodsStartDate) {
    options.push({
      value: DAYS_BEFORE_START,
      label: $t('Days before :date', { date: formatPeriodDate(store.paymentRequestPeriodsStartDate) }),
    })
  }

  return options
})

const selectedPeriod = computed({
  get: () => {
    if (billsDaysBeforeStart.value) {
      return periodOptions.value.find((option) => option.value === DAYS_BEFORE_START) ?? null
    }

    const { dateStart, dateEnd } = store.paymentRequestForm
    const value = dateStart && dateEnd ? periodValue(dateStart, dateEnd) : null

    return periodOptions.value.find((option) => option.value === value) ?? null
  },
  set: (option: { value: string; label: string } | string | null) => {
    const value = option && typeof option === 'object' ? option.value : option
    picksDaysBeforeStart.value = value === DAYS_BEFORE_START
    const [dateStart, dateEnd] =
      value && value !== DAYS_BEFORE_START ? value.split('|') : [undefined, '']
    store.paymentRequestForm.dateStart = dateStart
    store.paymentRequestForm.dateEnd = dateEnd ?? ''

    if (picksDaysBeforeStart.value) {
      validationStore.v$.dateStart.$reset()
      validationStore.v$.dateEnd.$reset()
    } else {
      validationStore.v$.dateStart.$touch()
    }
  },
})

const readOnlyPeriodLabel = computed(() => {
  const { dateStart, dateEnd } = store.paymentRequestForm

  if (!dateStart || !dateEnd) {
    return $t('None')
  }

  const period = store.paymentRequestPeriods.find(
    (part) => part.startDate === dateStart && part.endDate === dateEnd,
  )

  return paymentRequestPeriodLabel(period?.isoWeek ?? null, dateStart, dateEnd)
})
</script>
